import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

const WALK_IMG = 'https://BelvoirCastle.b-cdn.net/Events/Dove%20Cottage%20Winter%20Walk%20(1).webp';
const GARDENS_IMG = 'https://BelvoirCastle.b-cdn.net/Events/CAG_Morning_Light_Belvoir_Castle_Leicestershire-004.webp';
const DOVE_IMG = 'https://BelvoirCastle.b-cdn.net/Events/1793107645058-76111b16.webp';
const DOVE_URL = 'https://dovecottage.org/';
const BOOK_URL = '/booking-journey?locationId=978&pageId=76c3a1f1-df9b-4142-b0a5-e90aea662b13';
const PAGE_PATH = 'whats-on/charity-walk';

const C = {
  navy: '#101d40',
  midnight: '#0A1428',
  gold: '#C9963C',
  goldDk: '#A67C2A',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(560px, 72vh, 760px);
  overflow: hidden;
  display: flex;
  align-items: center;
  background: ${C.navy};

  @media (max-width: 768px) {
    height: auto;
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeroImg = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 62%;
  height: 100%;
  object-fit: cover;
  object-position: center;

  @media (max-width: 768px) {
    position: relative;
    width: 100%;
    height: auto;
    aspect-ratio: 3 / 2;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    ${C.navy} 0%,
    ${C.navy} 38%,
    rgba(16, 29, 64, 0.6) 46%,
    transparent 60%
  );

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(1.75rem, 4.5vw, 3rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroText = styled.div`
  max-width: min(34rem, 44vw);

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const HeroKicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.55rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.4rem, 5.8vw, 4.2rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);

  em {
    font-style: italic;
    color: ${C.gold};
    text-shadow: 0 1px 3px rgba(10, 20, 40, 0.75), 0 4px 16px rgba(10, 20, 40, 0.7);
  }
`;

const HeroStrapline = styled.p`
  margin: 0 0 1.15rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.35rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  max-width: 38rem;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 1.15rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => (p.$fill ? C.gold : 'rgba(16, 29, 64, 0.55)')};
  color: ${p => (p.$fill ? C.navy : '#fff')};
  border: 1px solid ${p => (p.$fill ? 'transparent' : 'rgba(255, 255, 255, 0.35)')};
  backdrop-filter: blur(10px);
`;

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;

    a,
    button {
      width: 100%;
    }
  }
`;

const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: ${C.gold};
  color: ${C.navy};
  border: none;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${C.goldDk};
    color: #fff;
    transform: translateY(-1px);
  }
`;

const BtnGhost = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => (p.$border ? '1px solid rgba(16, 29, 64, 0.07)' : 'none')};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 2.5rem;
  height: 2px;
  background: ${C.gold};
  margin-bottom: 1.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${C.navy};

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const SectionIntro = styled.p`
  margin: 0 0 1.25rem;
  max-width: 38rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.68);

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${C.goldDk};
    font-weight: 600;
  }
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.75rem, 4vw, 3.5rem);
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Photo = styled.div`
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 3 / 2;
  order: ${p => (p.$first ? -1 : 0)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 800px) {
    order: 0;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 12px;
  overflow: hidden;
  margin-top: clamp(1.5rem, 3vw, 2.5rem);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  padding: clamp(1.4rem, 2.8vw, 2rem);
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.62);
`;

const ActionBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(1.25rem, 3vw, 2.5rem);
  margin-top: clamp(1.5rem, 3vw, 2.5rem);
  background: #fff;
  border-radius: 12px;
  padding: clamp(1.4rem, 2.8vw, 2rem);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ActionTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.55rem;
  font-weight: 700;
  color: ${C.navy};
`;

const ActionText = styled.p`
  margin: 0;
  max-width: 40rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.68);
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;

    a {
      width: 100%;
    }
  }
`;

const ActionOutline = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: ${C.navy};
  border: 1.5px solid rgba(16, 29, 64, 0.35);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: ${C.navy};
    background: ${C.navy};
    color: #fff;
  }
`;

const ComingSoon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  border-radius: 6px;
  background: ${p => (p.$light ? 'rgba(255, 255, 255, 0.12)' : C.parchment)};
  color: ${p => (p.$light ? '#fff' : C.navy)};
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const InfoLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.4);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const InfoLink = styled(Link)`
  display: inline-block;
  margin-top: 0.35rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${C.goldDk};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: ${C.navy};
  }
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(201, 150, 60, 0.08) 38px, rgba(201, 150, 60, 0.08) 40px),
      repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(16, 29, 64, 0.2) 38px, rgba(16, 29, 64, 0.2) 40px);
    pointer-events: none;
  }
`;

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.6;
`;

const CtaMeta = styled.p`
  margin: 1.5rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
`;

const BookButton = ({ light }) => (
  BOOK_URL
    ? <BtnPrimary href={BOOK_URL}>Book tickets</BtnPrimary>
    : <ComingSoon $light={light}>Tickets on sale soon</ComingSoon>
);

const CharityWalk = () => {
  const navigate = useNavigate();
  const canonical = getCanonicalUrl(PAGE_PATH);

  return (
    <>
      <Helmet>
        <title>Charity Walk for Dove Cottage at Belvoir Castle | Sunday 6 December 2026</title>
        <meta
          name="description"
          content="Join our Charity Walk for Dove Cottage Day Hospice's 30th anniversary on Sunday 6 December 2026. Walk the Duke's Walk at Belvoir Castle. £5 per person, donated in full. Dogs on leads welcome."
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Charity Walk for Dove Cottage at Belvoir Castle" />
        <meta
          property="og:description"
          content="Sunday 6 December 2026. Walk the Duke's Walk to celebrate 30 years of Dove Cottage Day Hospice. £5 per person, donated in full."
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={WALK_IMG} />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: 'Charity Walk for Dove Cottage',
            description:
              "A charity walk on the Duke's Walk at Belvoir Castle in partnership with Dove Cottage Day Hospice, celebrating its 30th anniversary. Ticket sales are donated in full to Dove Cottage.",
            image: [WALK_IMG, GARDENS_IMG, DOVE_IMG],
            url: canonical,
            startDate: '2026-12-06T10:00:00',
            endDate: '2026-12-06T16:00:00',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            isAccessibleForFree: false,
            offers: {
              '@type': 'Offer',
              price: '5',
              priceCurrency: 'GBP',
              availability: 'https://schema.org/InStock',
              url: canonical,
            },
            location: {
              '@type': 'Place',
              name: 'Belvoir Castle',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                postalCode: 'NG32 1PE',
                addressRegion: 'Leicestershire',
                addressCountry: 'GB',
              },
            },
            organizer: [
              { '@type': 'Organization', name: 'Belvoir Castle', url: 'https://www.belvoircastle.com' },
              { '@type': 'Organization', name: 'Dove Cottage Day Hospice', url: DOVE_URL },
            ],
          })}
        </script>
      </Helmet>

      <Page>
        <HeroWrap>
          <HeroImg src={WALK_IMG} alt="Two walkers in winter coats on a woodland path in the grounds of Belvoir Castle" />
          <HeroOverlay />
          <HeroContent>
            <HeroText>
              <HeroKicker>The Duke’s Walk · Sunday 6 December 2026</HeroKicker>
              <HeroTitle>
                Charity Walk for <em>Dove Cottage</em>
              </HeroTitle>
              <HeroStrapline>
                Join us as Dove Cottage Day Hospice celebrates its 30th anniversary, with a walk through the grounds of Belvoir Castle.
              </HeroStrapline>
              <HeroBadgeRow>
                <Badge $fill>£5 per person</Badge>
                <Badge>Donated in full</Badge>
                <Badge>Dogs on leads welcome</Badge>
              </HeroBadgeRow>
              <HeroButtons>
                <BookButton light />
                <BtnGhost
                  type="button"
                  onClick={() => document.getElementById('charity-walk-about')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  About the walk
                </BtnGhost>
              </HeroButtons>
            </HeroText>
          </HeroContent>
        </HeroWrap>

        <Section $bg={C.ivory} $border id="charity-walk-about">
          <Inner>
            <TwoCols>
              <div>
                <Kicker>In partnership with Dove Cottage</Kicker>
                <Divider />
                <SectionTitle>
                  A winter walk on
                  <br />
                  <em>the Belvoir Estate.</em>
                </SectionTitle>
                <SectionIntro>
                  Join us on Sunday 6th December for our Charity Walk in partnership with Dove Cottage, as the charity celebrates its 30th anniversary. Explore the grounds of Belvoir Castle on the Duke’s Walk, a route through sweeping parkland and rolling hills.
                </SectionIntro>
                <SectionIntro>
                  Wrap up warm and bring the whole family. Dogs on leads are welcome.
                </SectionIntro>
              </div>
              <Photo>
                <img src={GARDENS_IMG} style={{ objectPosition: 'right center' }} alt="A visitor in a winter hat admiring a blossoming cherry tree in the gardens at Belvoir Castle at sunrise" />
              </Photo>
            </TwoCols>

            <FeatureGrid>
              <FeatureCard>
                <FeatureTitle>The Duke’s Walk</FeatureTitle>
                <FeatureBody>A route through the sweeping parkland and rolling hills of the Belvoir estate.</FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>Bring the whole family</FeatureTitle>
                <FeatureBody>Wrap up warm and make a winter day of it, all in support of a local charity.</FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>Dogs on leads welcome</FeatureTitle>
                <FeatureBody>Bring your dog along for the walk, kept on a lead throughout the grounds.</FeatureBody>
              </FeatureCard>
            </FeatureGrid>
          </Inner>
        </Section>

        <Section $bg="#fff" $border>
          <Inner>
            <TwoCols>
              <Photo $first>
                <img src={DOVE_IMG} alt="Dove Cottage Day Hospice logo over a winter woodland at sunrise" />
              </Photo>
              <div>
                <Kicker>30 years of care</Kicker>
                <Divider />
                <SectionTitle>
                  About <em>Dove Cottage</em>
                </SectionTitle>
                <SectionIntro>
                  Dove Cottage Day Hospice has been caring for people in our local community since 1996. It provides day care to adults living with life-limiting illnesses such as cancer, heart failure, motor neurone disease and COPD, supporting up to 100 families each week across north east Leicestershire, Rutland, south east Nottinghamshire and south west Lincolnshire.
                </SectionIntro>
                <SectionIntro>
                  All of its care is provided free of charge, so it relies on the generosity of supporters like you to keep its doors open. Ticket sales will support Dove Cottage’s care services in our community, as the charity marks 30 years of its work.
                </SectionIntro>
                <SectionIntro>
                  Find out more about the charity’s work on the{' '}
                  <a href={DOVE_URL} target="_blank" rel="noopener noreferrer">
                    Dove Cottage website
                  </a>
                  .
                </SectionIntro>
              </div>
            </TwoCols>

            <ActionBox>
              <div>
                <ActionTitle>Tickets £5 per person</ActionTitle>
                <ActionText>
                  Every ticket is donated in full to Dove Cottage. Friends of Belvoir receive free entry to the grounds and are welcome to make a donation to Dove Cottage on the day.
                </ActionText>
              </div>
              <ActionButtons>
                <BookButton />
                <ActionOutline to="/memberships">Friends of Belvoir</ActionOutline>
              </ActionButtons>
            </ActionBox>
          </Inner>
        </Section>

        <Section $bg={C.parchment} $border>
          <Inner>
            <Kicker>Plan Your Visit</Kicker>
            <Divider />
            <SectionTitle>
              Essential <em>Information</em>
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Date</InfoLabel>
                <InfoValue>
                  Sunday
                  <br />
                  6 December 2026
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Tickets</InfoLabel>
                <InfoValue>
                  £5 per person
                  <br />
                  Donated in full to Dove Cottage
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Parking</InfoLabel>
                <InfoValue>
                  £4 all day
                  <br />
                  Main Castle Car Park
                </InfoValue>
                <InfoLink to="/about/parking">Parking information</InfoLink>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Dove Cottage team</InfoLabel>
                <InfoValue>
                  Ticket Office Gates
                  <br />
                  10am – 4pm
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Dogs</InfoLabel>
                <InfoValue>Welcome on leads</InfoValue>
                <InfoLink to="/about/dog-friendly">Read our dog policy</InfoLink>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Friends of Belvoir</InfoLabel>
                <InfoValue>
                  Free entry to the grounds
                  <br />
                  Donations welcome
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </Inner>
        </Section>

        <CtaSection>
          <CtaInner>
            <CtaTitle>
              Walk with us for
              <br />
              <em>30 years of Dove Cottage.</em>
            </CtaTitle>
            <CtaBody>
              Sunday 6th December on the Duke’s Walk at Belvoir Castle. Tickets are £5 per person, donated in full to Dove Cottage.
            </CtaBody>
            <HeroButtons style={{ justifyContent: 'center' }}>
              {BOOK_URL ? (
                <BtnPrimary href={BOOK_URL}>Book tickets</BtnPrimary>
              ) : (
                <BtnGhost type="button" onClick={() => navigate('/your-visit')}>
                  Plan your visit
                </BtnGhost>
              )}
            </HeroButtons>
            <CtaMeta>
              Sunday 6 December 2026 &nbsp;·&nbsp; The Duke’s Walk &nbsp;·&nbsp; Dove Cottage team at the Ticket Office Gates 10am – 4pm
            </CtaMeta>
          </CtaInner>
        </CtaSection>

        <Footer />
      </Page>
    </>
  );
};

export default CharityWalk;
